import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/** Toast-Typ fuer visuelles Feedback */
export type ToastType = 'success' | 'error' | 'info';

/** Einzelner Toast-Eintrag */
export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

/** Oeffentliche Toast-API */
export type ToastAPI = (message: string, type?: ToastType) => void;

const ToastContext = createContext<ToastAPI | null>(null);

/**
 * Provider-Komponente die das Toast-System initialisiert.
 * Muss einmalig in der App-Root eingebunden werden.
 *
 * @param props.children - Child-Komponenten
 * @returns Provider mit Toast-Container
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message: string, type: ToastType = 'info') => {
    counterRef.current += 1;
    const id = `toast-${counterRef.current}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Einzelne Toast-Anzeige-Komponente */
function ToastItem({ toast, onRemove }: { toast: ToastItem; onRemove: (id: string) => void }) {
  const labelId = useId();

  useEffect(() => {
    return () => {
      // Cleanup falls unmount vor Timer
    };
  }, []);

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-labelledby={labelId}
      className={cn(
        'pointer-events-auto flex items-start gap-3 px-4 py-3 rounded shadow-lg',
        'bg-card border-2 border-ink/10 border-l-4',
        toast.type === 'success' && 'border-l-okay',
        toast.type === 'error' && 'border-l-accent',
        toast.type === 'info' && 'border-l-brand',
      )}
    >
      <span id={labelId} className="text-sm text-ink flex-1">
        {toast.message}
      </span>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        aria-label="Toast schliessen"
        className="flex-shrink-0 text-ink-muted hover:text-ink transition-colors duration-150 cursor-pointer border-0 bg-transparent p-0"
      >
        <svg
          className="h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Hook zum Anzeigen von Toast-Benachrichtigungen.
 * Muss innerhalb eines ToastProvider verwendet werden.
 *
 * @returns Funktion zum Anzeigen eines Toasts
 * @throws Error wenn ausserhalb eines ToastProvider verwendet
 * @example
 * ```tsx
 * const toast = useToast();
 * toast('Ticket zugewiesen!', 'success');
 * toast('Fehler aufgetreten', 'error');
 * ```
 */
export function useToast(): ToastAPI {
  const ctx = useContext(ToastContext);
  if (ctx == null) {
    throw new Error('useToast muss innerhalb eines ToastProvider verwendet werden.');
  }
  return ctx;
}
